import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CouponTypes } from '@enums/coupon-types';
import { CustomerTestsTypes } from '@enums/customer-tests-types';
import { DataLayerCategories } from '@enums/data-layer-categories';
import { Sites } from '@enums/sites';
import { StorageKeys } from '@enums/storage-keys';
import { UpsellSlugs } from '@enums/upsell-slugs';
import { environment } from '@environments/environment';
import { ApiOrderRequest } from '@interfaces/api-order-request';
import { InHomeCollection } from '@interfaces/in-home-collection';
import { LoadCartOptions } from '@interfaces/load-cart-options';
import { OrderDetailsUpdate } from '@interfaces/order-details-update';
import { InHomeBookingResult, InHomeCollectionService, Result, ResultService } from '@Medology/ng-findalab';
import { GiftCardPayment, Payment as NgxPaymentPayment } from '@medology/ngx-payment';
import { Address } from '@models/address';
import { AnalyteCareCouponDto } from '@models/analyte-care-coupon-dto';
import { AnalyteCareLoadCartRequest } from '@models/analyte-care-load-cart-request';
import { AnalyteCareLoadCartResponse } from '@models/analyte-care-load-cart-response';
import { CartAdditionalItem } from '@models/cart-additional-item';
import { Coupon } from '@models/coupon';
import { CustomerNote } from '@models/customer-note';
import { ExtraCharge } from '@models/extra-charge';
import { HasBetterLabShowResponse } from '@models/has-better-lab-show-response';
import { LegacyUpsellStoreResponse } from '@models/legacy-upsell-store-response.model';
import { LoadCartRequest } from '@models/load-cart-request';
import { LoadCartResponse } from '@models/load-cart-response';
import { OrderDiscount } from '@models/order-discount';
import { OrderRequest } from '@models/order-request';
import { OrderResponse } from '@models/order-response';
import { OrderTest } from '@models/order-test';
import { OrderUpgradeResponse } from '@models/order-upgrade-response';
import { OrderUpgradeSchema } from '@models/order-upgrade-schema';
import { OrderUpsell } from '@models/order-upsell';
import { PaginatedResponse } from '@models/paginatedResponse';
import { Payment } from '@models/payment';
import { RechargeRequest } from '@models/recharge-request';
import { Reorder } from '@models/reorder';
import { ReorderAddress } from '@models/reorder-address';
import { AddressUpdateRequest } from '@models/requests/address-update.model';
import { LegacyUpsellStoreRequest } from '@models/requests/legacy-upsell-store.model';
import { PaymentGiftCardStoreRequest } from '@models/requests/payment-gift-card-store-request.model';
import { ShippingAddressStoreRequest } from '@models/requests/shipping-address-store.model';
import { UpsellBatchStoreRequest } from '@models/requests/upsell-batch-store.model';
import { UpsellStoreRequest } from '@models/requests/upsell-store.model';
import { ShippingAddressStoreResponse } from '@models/responses/shipping-address-store-response.model';
import { UpsellBatchStoreResponse } from '@models/responses/upsell-batch-store-response.model';
import { ShippingAddress } from '@models/shippingAddress';
import { Test } from '@models/test';
import { TransactionResponse } from '@models/transaction-response';
import { Upgrade } from '@models/upgrade';
import { UpgradeResponse } from '@models/upgrade-response';
import { AuthorizationService } from '@services/authorization.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { DataLayerService } from '@services/data-layer.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { FormService } from '@services/form.service';
import { GraphqlService } from '@services/graphql.service';
import { PaymentService } from '@services/payment.service';
import { QueryParamsService } from '@services/query-params.service';
import { StorageService } from '@services/storage.service';
import { TreatmentTypeService } from '@services/treatment-type.service';
import { EMPTY, firstValueFrom, forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ReorderAddressDataDocument } from '../graphql/queries/reorder-address-data.query';
import { ReorderDataDocument } from '../graphql/queries/reorder-data.query';

@Injectable({
  providedIn: 'root',
})
export abstract class OrderService {
  public placeOrderButtonDisable: boolean = false;
  public rechargeData: RechargeRequest = null;
  public giftCardApplied = 0;
  public giftCardAppliedSubject$ = new Subject<number>();
  public labResultSubscription: Subscription;

  protected document = inject(DOCUMENT);
  protected http = inject(HttpClient);
  protected router = inject(Router);
  protected storage = inject(StorageService);
  protected results = inject(ResultService);
  protected formService = inject(FormService);
  protected authService = inject(AuthorizationService);
  protected inHomeService = inject(InHomeCollectionService);
  protected dataLayerService = inject(DataLayerService);
  protected consultationStorageService = inject(ConsultationStorageService);
  protected errorLoggingService = inject(ErrorLoggingService);
  protected treatmentTypeService = inject(TreatmentTypeService);
  protected graphqlService = inject(GraphqlService);
  protected queryParamsService = inject(QueryParamsService);

  protected apiUrl = environment.analyteCareApi;
  protected checkoutForm: FormGroup;
  protected partnerDiscount: number = 1000;
  protected orderPlacedRedirectPage: string = '/order-address.php';

  private paymentService = inject(PaymentService);

  public constructor() {
    this.checkoutForm = this.formService.checkout;
    // Update the order when lab is selected/changed
    this.labResultSubscription = this.listenForLabSelection();
  }

  /**
   * Gets the order extra charges.
   */
  public get extraCharges(): ExtraCharge[] {
    return this.storage.extraCharges;
  }

  /**
   * Gets the order upsells.
   */
  public get orderUpsells(): OrderUpsell[] {
    return this.storage.orderUpsells;
  }

  /**
   * Get the tests from the url.
   */
  public get testsFromUrl(): string[] {
    const url = new URL(document.location.href);
    const searchParams = url.searchParams;

    const panel = searchParams.get('panel');
    if (panel) {
      return panel.split(',');
    }

    const tests = searchParams.get('tests');
    if (tests) {
      return tests.split(',');
    }

    const panelArr = searchParams.getAll('panel[]');
    if (panelArr.length) {
      return panelArr;
    }

    const testsArr = searchParams.getAll('tests[]');
    if (testsArr.length) {
      return testsArr;
    }

    return [];
  }

  /**
   * Getter for transaction_id
   */
  public get transactionId(): string {
    return this.storage.transactionId;
  }

  /**
   * Gets the coupon code from the url or the storage, or returns null if there is no coupon.
   */
  public get couponCode(): string | null {
    const url = new URL(document.location.href);

    return url.searchParams.get('coupon') || this.storage.coupon?.coupon_code || null;
  }

  /**
   * Gets the account token to send with the load cart request. Returns null because most sites do not support
   * business account pricing on the cart, so they must not send a token. Sites that do support it override this.
   */
  public get authToken(): string | null {
    return null;
  }

  /**
   * Creates an InHomeCollection object from an InHomeBookingResult object.
   *
   * @param {InHomeBookingResult} inHomeBookingResult
   */
  public static inHomeCollectionFromInHomeBookingResult(inHomeBookingResult: InHomeBookingResult): InHomeCollection {
    return {
      appointment_key: inHomeBookingResult.bookingSlot.key,
      street: inHomeBookingResult.address.street,
      city: inHomeBookingResult.address.city,
      state: inHomeBookingResult.address.state,
      zip_code: inHomeBookingResult.address.zipCode,
      provider_id: inHomeBookingResult.bookingSlot.providerId,
    };
  }

  /**
   * Get the order submit validation errors.
   */
  public getOrderSubmitValidationErrors(): string[] {
    return [];
  }

  /**
   * Load cart request.
   *
   * We are able to retrieve the current cart
   *
   * @param {LoadCartRequest} request Load Cart Request containing the parameters to load the cart
   *
   * @returns {Observable<LoadCartResponse>} an Observable of the LoadCartResponse.
   */
  public loadCartRequest(request: LoadCartRequest): Observable<AnalyteCareLoadCartResponse> {
    return this.http
      .get<LoadCartResponse>(`${environment.apiUrl}/load-cart`, {
        params: request.getHttpParams(),
        headers: request.getHttpHeaders(),
      })
      .pipe((request) => this.handleLoadCartResponse(request));
  }

  /**
   * Gets whether the user applied partner testing or not.
   */
  public hasPartner(): boolean {
    return this.storage.hasPartner;
  }

  /**
   * Gets the discount amount to apply based on coupons for the specified amount.
   *
   * @param {number} amount the amount to calculate the coupon discount for
   *
   * @returns {number} the total coupon discount calculated
   */
  public getTotalCoupons(amount: number): number {
    if (!this.storage.coupon) {
      return 0;
    }

    const couponAmount = this.storage.coupon.coupon_amount;

    if (this.isInvalidNumber(couponAmount)) {
      this.logItemError('Coupon has an invalid amount.', this.storage.coupon);

      return 0;
    }

    if (this.storage.coupon.coupon_type == CouponTypes.Percentage) {
      return (Number(couponAmount) / 100) * amount;
    }

    return Number(couponAmount) * 100;
  }

  /**
   * Clears the storage data AFTER an order has been successfully completed.
   */
  public clearLastOrder(): void {
    this.resetOrderStorage();
    this.storage.remove(StorageKeys.TransactionId, false);
    this.storage.remove(StorageKeys.Coupon);
    this.storage.remove(StorageKeys.Free);
    this.storage.remove(StorageKeys.Tests);
    this.storage.remove(StorageKeys.Email);
    this.storage.remove(StorageKeys.InHomeBookingResult);
    this.storage.remove(StorageKeys.TreatmentType);
    this.storage.remove(StorageKeys.ExtraCharges);
    this.storage.remove(StorageKeys.OrderUpsells);
    this.storage.remove(StorageKeys.IsShipped);
    this.storage.remove(StorageKeys.ReorderId);
  }

  /**
   * Resets the storage to a clean state BEFORE starting a new order flow.
   */
  public resetOrderStorage(): void {
    this.storage.remove(StorageKeys.Fee);
    this.storage.remove(StorageKeys.AddressComplete);
    this.storage.remove(StorageKeys.PaymentType);
    this.storage.remove(StorageKeys.LabChanged);
    this.storage.remove(StorageKeys.OrderType, false);
    this.storage.remove(StorageKeys.Patient);
    this.storage.remove(StorageKeys.OrderTotal);
    this.storage.remove(StorageKeys.HasEmail);
    this.storage.remove(StorageKeys.SendSms);
    this.storage.remove(StorageKeys.UpsellTransactionId, false);
    this.storage.remove(StorageKeys.SecondaryPaymentTransactionId, false);
    this.storage.remove(StorageKeys.Order, false);
    this.storage.remove(StorageKeys.BetterLab);
    this.storage.remove(StorageKeys.OrderedTests);
  }

  /**
   * Calculates the total amount of the order, taking into account the subtotal,
   * any applicable fees, and discounts.
   *
   * @returns the total amount of the order
   */
  public getTotal(): number {
    const testsTotal = this.getTestsTotal();
    const partnerTotal = this.getPartnerTotal();

    return Math.max(
      0,
      testsTotal +
        partnerTotal +
        this.getExtraChargesTotal() +
        this.getOrderUpsellsTotal() +
        this.getInHomeCollectionTotal() +
        this.getTotalComplianceFee() -
        this.getTotalDiscount() -
        this.getTotalCoupons(testsTotal + partnerTotal),
    );
  }

  /**
   * Get available upgrade.
   */
  public getUpgrade(request: AnalyteCareLoadCartRequest): Observable<UpgradeResponse> {
    return this.http.post<UpgradeResponse>(`${this.apiUrl}/order-upgradable-tests`, request);
  }

  /**
   * Submits the order.
   *
   * @param {string} deviceData the device data from Braintree
   * @param {NgxPaymentPayment[]} payments the payment data from the payment package
   * @param {boolean} isExternalSubscriptionOrder whether the order is billed through the external subscription
   * service, submitting all payments in a single request
   */
  public submitOrder(
    deviceData: string,
    payments: NgxPaymentPayment[],
    isExternalSubscriptionOrder: boolean = false,
  ): Observable<OrderResponse> {
    const request = this.getOrderRequest(deviceData, payments, isExternalSubscriptionOrder);

    return this.http.post<OrderResponse>(
      `${this.apiUrl}${request.orderingEndpoint}`,
      request,
      this.authService.getOptionsWithAuthHeader(),
    );
  }

  /**
   * Make an additional payment to an order with the gift card applied.
   */
  public makeSecondaryPaymentWithGiftCard(
    orderId: string,
    giftCardPayment: GiftCardPayment,
  ): Promise<TransactionResponse> {
    const request = new PaymentGiftCardStoreRequest(giftCardPayment.code, giftCardPayment.balance * 100, orderId);

    return firstValueFrom(
      this.http.post<TransactionResponse>(
        `${this.apiUrl}/api/v1/order/${request.order_id}/payment`,
        request,
        this.authService.getOptionsWithAuthHeader(),
      ),
    );
  }

  /**
   * Gets the details of the order to reorder.
   *
   * @param {string} transactionId the transaction ID of the order to reorder
   * @param {string} token the account token cookie value
   *
   * @returns {Observable<Reorder>} an observable that emits a Reorder object
   */
  public getReorder(transactionId: string, token: string): Observable<Reorder> {
    return this.graphqlService
      .execute(ReorderDataDocument, { transactionId }, this.authService.getOptionsWithAuthHeader(token))
      .pipe(
        switchMap(({ orderByTransactionId }) => {
          if (!orderByTransactionId) {
            this.errorLoggingService.logError(`No data received from GraphQL endpoint for re-order: ${transactionId}.`);
            this.navigateToOrderErrorPage();
          }

          return of(this.mapResponseToReorder(orderByTransactionId));
        }),
        catchError((error) => {
          this.errorLoggingService.logError(`Error while fetching re-order - ${error}`);
          this.navigateToOrderErrorPage();

          return EMPTY;
        }),
      );
  }

  /**
   * Gets the address data of the order to reorder.
   *
   * @param {string} transactionId the transaction ID of the order to reorder
   * @param {string} token the account token cookie value
   */
  public getReorderAddressData(transactionId: string, token: string): Observable<ReorderAddress> {
    return this.graphqlService
      .execute(ReorderAddressDataDocument, { transactionId }, this.authService.getOptionsWithAuthHeader(token))
      .pipe(map(({ orderByTransactionId }) => orderByTransactionId));
  }

  /**
   * Stores an order address.
   *
   * @param addressUpdateRequest data from order address form
   */
  public storeAddress(addressUpdateRequest: AddressUpdateRequest): Observable<any> {
    const body = {
      ...addressUpdateRequest,
      phone: {
        ...addressUpdateRequest.phone,
        sendSms: addressUpdateRequest.phone.sendSms.toString(),
        leaveMsg: addressUpdateRequest.phone.leaveMsg.toString(),
      },
      agreeTerms: addressUpdateRequest.agreeTerms.toString(),
    };

    return this.http.post(`${this.apiUrl}/api/v1/order/${this.transactionId}/address`, body);
  }

  /**
   * Updates an order address.
   *
   * @param {Address} address data from order address form
   * @param {string} transactionId the transaction ID of the order
   * @param {string} hash the hash of the order
   */
  public updateAddress(address: Address, transactionId: string, hash: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/api/v1/order/${transactionId}/address`, address, {
      headers: new HttpHeaders({ 'X-API-Hash': hash }),
    });
  }

  /**
   * Updates the shipping address of an order.
   *
   * @param {ShippingAddress} shippingAddress data from order address form
   * @param {string} transactionId the transaction ID of the order
   * @param {string} hash the hash of the order
   */
  public updateShippingAddress(
    shippingAddress: Partial<ShippingAddress>,
    transactionId: string,
    hash: string,
  ): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/api/v1/order/${transactionId}/shipping-address/${shippingAddress.id}`,
      shippingAddress,
      {
        headers: new HttpHeaders({ 'X-API-Hash': hash }),
      },
    );
  }

  /**
   * Store the payment information, the payment will be charged in the API.
   * This method is used to store the additional payment.
   *
   * @param {OrderUpsell} orderUpsell   the order upsell
   * @param {string}      transactionId the transaction ID of the order
   * @param {string}      hash          the hash of the order
   */
  public storeUpsell(orderUpsell: OrderUpsell, transactionId: string, hash: string): Observable<number> {
    return this.http.post<number>(
      `${this.apiUrl}/api/v1/order/${transactionId}/upsells`,
      new UpsellStoreRequest(orderUpsell),
      { headers: new HttpHeaders({ 'X-API-Hash': hash }) },
    );
  }

  /**
   * Stores every given upsell on the order in a single request.
   *
   * The API adds the whole batch atomically, so either all of the upsells are stored (charged in case of subscription)
   * or none of them are.
   *
   * @param {UpsellBatchStoreRequest} request       the batch of upsells to add to the order
   * @param {string}                  transactionId the transaction ID of the order
   * @param {string}                  hash          the hash of the order
   *
   * @returns {Observable<UpsellBatchStoreResponse[]>} the created customer tests, each one paired with the ID of the
   *                                                   test it was created from
   */
  public storeUpsellBatch(
    request: UpsellBatchStoreRequest,
    transactionId: string,
    hash: string,
  ): Observable<UpsellBatchStoreResponse[]> {
    return this.http.patch<UpsellBatchStoreResponse[]>(
      `${this.apiUrl}/api/v1/order/${transactionId}/upsells`,
      request,
      { headers: new HttpHeaders({ 'X-API-Hash': hash }) },
    );
  }

  /**
   * Get a list of tests available for upsell.
   *
   * @param {number[]} testIds the ids of the tests to get upsells for
   */
  public getOrderUpsells(testIds: number[]): Observable<OrderUpsell[]> {
    return this.http
      .get<OrderUpsell[]>(`${this.apiUrl}/api/v1/upsells`, {
        params: this.getUpsellParams(testIds),
      })
      .pipe(
        map((response: OrderUpsell | OrderUpsell[]) =>
          response === null ? [] : Array.isArray(response) ? response : [response],
        ),
      );
  }

  /**
   * Stores the in-home collection appointment for an existing order.
   *
   * @param {string}           transactionId        Order's transaction ID
   * @param {string}           hash                 The hash of the order
   * @param {InHomeCollection} inHomeCollectionData Data for the in-home collection appointment
   */
  public storeInHomeCollectionAppointment(
    transactionId: string,
    hash: string,
    inHomeCollectionData: InHomeCollection,
  ): Observable<number> {
    return this.http.post<number>(
      `${this.apiUrl}/api/v1/order/${transactionId}/appointment`,
      { in_home_collection: inHomeCollectionData },
      { headers: new HttpHeaders({ 'X-API-Hash': hash }) },
    );
  }

  /**
   * Update Current Order lab
   *
   * @param {number}  centerId the id of the center to update the order to
   * @param {string}  hash the hash of the order
   * @param {string?} transactionId the transaction id of the order (optional)
   */
  public updateLab(centerId: number, hash: string, transactionId?: string): Observable<{ success: Boolean }> {
    const usedTransactionId = transactionId || this.transactionId;

    return this.http.put<{ success: Boolean }>(
      `${this.apiUrl}/api/v1/order/${usedTransactionId}/lab`,
      { center_id: centerId },
      { headers: new HttpHeaders({ 'X-API-Hash': hash }) },
    );
  }

  /**
   * Applies the current gift card to the cart.
   *
   * @param balance gift card balance
   */
  public applyGiftCard(balance: number): void {
    const total = this.getTotal();
    this.giftCardApplied = balance ? (total < balance * 100 ? total : balance * 100) : 0;
    this.giftCardAppliedSubject$.next(this.giftCardApplied);
  }

  /**
   * Gets the payment recharge information
   *
   * @param {string} token The JWT token
   */
  public getPaymentRechargeable(token: string): Observable<RechargeRequest> {
    return this.http.get<any>(`${this.apiUrl}/payment-rechargeable`, this.authService.getOptionsWithAuthHeader(token));
  }

  /**
   * Update storage from response.
   */
  public updateStorage(response: AnalyteCareLoadCartResponse | OrderUpgradeResponse): void {
    this.storage.tests = response.tests;
    this.storage.fee = response.fee;
    this.storage.coupon = response.coupon;

    if (response.center) {
      this.storage.center = response.center;
      this.formService.checkout.get('lab_id').setValue(this.storage.center.id);
    }
  }

  /**
   * Places an upsell order using the legacy API.
   *
   * @param { LegacyUpsellStoreRequest } upsellStoreRequest data containing upsells to store
   * @param { string }                   transactionId      the transaction ID of the order
   */
  public legacyStoreUpsell(
    upsellStoreRequest: LegacyUpsellStoreRequest,
    transactionId: string = this.transactionId,
  ): Observable<LegacyUpsellStoreResponse> {
    if (Array.isArray(upsellStoreRequest.upsell) && upsellStoreRequest.upsell.length === 0) {
      return of(null);
    }

    return this.http.post<LegacyUpsellStoreResponse>(
      `${this.apiUrl}/api/v1/order/${transactionId}/upsell`,
      upsellStoreRequest,
    );
  }

  /**
   * Submits the upsells of an order in a single batch request and stores the data derived from them.
   *
   * @param {OrderUpsell[]} upsells array of upsells to submit
   * @param {string} orderId the order identifier
   * @param {string} hash the order hash
   *
   * @returns {Observable<UpsellBatchStoreResponse[]>} the customer tests created for the stored upsells
   */
  public submitUpsells(upsells: OrderUpsell[], orderId: string, hash: string): Observable<UpsellBatchStoreResponse[]> {
    const upsellsToStore = upsells.filter((upsell) => !upsell.skipOrderAddon);

    if (!upsellsToStore.length) {
      return of([]);
    }

    const request = new UpsellBatchStoreRequest(upsellsToStore);

    return this.storeUpsellBatch(request, orderId, hash).pipe(
      switchMap((createdTests) =>
        this.storeUpsellFollowUps(upsellsToStore, createdTests, orderId, hash).pipe(map(() => createdTests)),
      ),
    );
  }

  /**
   * Swallows an error and logs it to the console.
   *
   * @param {any} error the error to swallow
   */
  public swallowError(error: any): Observable<any> {
    this.errorLoggingService.logError(error);

    return of(null);
  }

  /**
   * Get the order has_better_lab flag.
   */
  public showOrderBetterLab(): Observable<HasBetterLabShowResponse> {
    return this.http.get<HasBetterLabShowResponse>(`${this.apiUrl}/api/v1/order/${this.transactionId}/has_better_lab`);
  }

  /**
   * Mark the order as completed so requisition is created by the API.
   */
  public orderCompleted(): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/v1/order/${this.transactionId}/completed`, {});
  }

  /**
   * Makes a request to the API to add a test to an order.
   *
   * @param {number} testId the ID of the test to add
   * @param {string} transactionId the transaction ID of the order
   * @param {string} hash the hash of the order
   *
   * @returns {Observable<string>} an observable that emits the transaction ID of the order
   */
  public addTestToOrder(testId: number, transactionId: string, hash: string): Observable<string> {
    return this.http.post<string>(
      `${this.apiUrl}/api/v1/order/${transactionId}/tests`,
      { test_id: testId },
      {
        headers: new HttpHeaders({ 'X-API-Hash': hash }),
      },
    );
  }

  /**
   * Makes a request to the API to remove a test from an order.
   *
   * @param {number} customerTestId the ID of the customer test to remove
   * @param {string} transactionId the transaction ID of the order
   * @param {string} hash the hash of the order
   */
  public removeTestFromOrder(customerTestId: number, transactionId: string, hash: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/api/v1/orders/${transactionId}/tests/${customerTestId}`, {
      headers: new HttpHeaders({ 'X-API-Hash': hash }),
    });
  }

  /**
   * Updates the details of an order.
   *
   * @param {string}              transactionId the transaction ID of the order
   * @param {string}              hash          the hash of the order
   * @param {OrderDetailsUpdate} data          the order fields to update (e.g. partner_email, phone, gender, due_date)
   */
  public updateOrderDetails(transactionId: string, hash: string, data: OrderDetailsUpdate): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/api/v1/order/${transactionId}`, data, {
      headers: new HttpHeaders({ 'X-API-Hash': hash }),
    });
  }

  /**
   * Get the order payments history information.
   *
   * @param {string} transactionId the transaction ID of the order
   * @param {string} hash the hash of the order
   */
  public orderPayments(transactionId: string, hash: string): Observable<Payment[]> {
    return this.http.get<Payment[]>(`${this.apiUrl}/api/v1/order/${transactionId}/payments`, {
      headers: new HttpHeaders({ 'X-API-Hash': hash }),
    });
  }

  /**
   * Get the order invoice file.
   *
   * @param {string} transactionId the transaction ID of the order
   * @param {string} hash the hash of the order
   */
  public downloadInvoice(transactionId: string, hash: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/api/v1/order/${transactionId}/invoice.pdf`, {
      headers: new HttpHeaders({ 'X-API-Hash': hash }),
      responseType: 'blob',
    });
  }

  /**
   * Returns the lab ID from the selected center.
   *
   * @returns {number} the center ID to use as lab ID
   */
  public getOrderLabId(): number {
    return this.storage.center?.id;
  }

  /**
   * Calculates the total of the order tests
   *
   * @returns {number} The total of the order tests
   */
  public getTestsTotal(): number {
    return this.storage.tests.reduce((sum, current) => {
      if (this.isInvalidNumber(current.price)) {
        this.logItemError('Test is missing a valid price.', current, this.storage.tests);

        return sum;
      }

      return sum + current.price;
    }, 0);
  }

  /**
   * Calculates the partner total on the order
   *
   * @returns {number} The partner total
   */
  public getPartnerTotal(): number {
    return this.hasPartner() ? this.getTestsTotal() : 0;
  }

  /**
   * Calculates the extra charges total on the order.
   *
   * @returns {number} the total of the extra charges
   */
  public getExtraChargesTotal(): number {
    return this.extraCharges.reduce((sum, current) => {
      if (this.isInvalidNumber(current.amount)) {
        this.logItemError('Extra charge is missing a valid amount.', current, this.extraCharges);

        return sum;
      }

      return sum + current.amount;
    }, 0);
  }

  /**
   * Calculates the total of the order upsells.
   *
   * @returns {number} the total of the order upsells
   */
  public getOrderUpsellsTotal(): number {
    return this.orderUpsells.reduce((sum, current) => {
      if (this.isInvalidNumber(current.upsell_price)) {
        this.logItemError('Order upsell is missing a valid price.', current, this.orderUpsells);

        return sum;
      }

      return sum + current.upsell_price;
    }, 0);
  }

  /**
   * Calculates the in home collection total on the order
   *
   * @returns {number} The in home collection total
   */
  public getInHomeCollectionTotal(): number {
    return this.storage.inHomeBookingResult ? environment.ngFindalab.inHomeCollectionCostInCents : 0;
  }

  /**
   * Calculates the compliance fee total
   *
   * @returns {number} The compliance fee total
   */
  public getTotalComplianceFee(): number {
    return this.storage.fee?.amount ?? 0;
  }

  /**
   * Stores an upsell test in the storage array if it doesn't already exist
   *
   * @param {OrderUpsell} upsellTest The upsell test object
   */
  public addUpsellTestToStorage(upsellTest: OrderUpsell): void {
    let tests = this.storage.orderedTests;
    const { id, name, price, slug } = upsellTest;

    if (tests.some((test) => test.name == name)) {
      return;
    }

    tests.push({ id, name, price, slug });
    this.storage.orderedTests = tests;
  }

  /**
   * Adds the upsell price to the order total
   *
   * @param {number} price The price to add to the order total
   */
  public addUpsellPriceToTotal(price: number): void {
    this.storage.orderTotal += price;
  }

  /**
   * Handle the redirection after order is placed
   *
   * @param {OrderResponse} response the Order Response
   */
  public afterOrderPlacement(response: OrderResponse): void {
    this.saveOrderValuesToLocalStorage(response);
    if (this.storage.free) {
      return this.updateAddressForFreeOrder();
    }

    this.dataLayerService.pushContactInfo(this.storage.patient.email, this.storage.patient.phone);

    this.redirectToNextPage();
  }

  /**
   * Update the shipping address
   *
   * @param {ShippingAddressStoreRequest} shippingAddressRequest The shipping address request
   * @param {string}                      transactionId          The transaction ID
   * @param {hash}                        hash                   The hash
   */
  public storeShippingAddress(
    shippingAddressRequest: ShippingAddressStoreRequest,
    transactionId: string,
    hash: string,
  ): Observable<ShippingAddressStoreResponse> {
    return this.http.post<ShippingAddressStoreResponse>(
      `${this.apiUrl}/api/v1/order/${transactionId}/shipping-address`,
      shippingAddressRequest,
      {
        headers: new HttpHeaders({ 'X-API-Hash': hash }),
      },
    );
  }

  /**
   * Store male bv discount in the order
   *
   * @param {OrderDiscount} discount      The discount to apply
   * @param {string}        transactionId The transaction ID
   * @param {hash}          hash          The hash
   */
  public storeMaleBVDiscount(
    discount: OrderDiscount,
    transactionId: string,
    hash: string,
  ): Observable<ShippingAddressStoreResponse> {
    return this.http.post<ShippingAddressStoreResponse>(
      `${this.apiUrl}/api/v1/order/${transactionId}/male-bv-discount`,
      discount,
      {
        headers: new HttpHeaders({ 'X-API-Hash': hash }),
      },
    );
  }

  /**
   * Store discount in the order
   *
   * @param {OrderDiscount} discount      The discount to apply
   * @param {string}        transactionId The transaction ID
   * @param {hash}          hash          The hash
   */
  public storeDiscount(
    discount: OrderDiscount,
    transactionId: string,
    hash: string,
  ): Observable<ShippingAddressStoreResponse> {
    return this.http.post<ShippingAddressStoreResponse>(
      `${this.apiUrl}/api/v1/order/${transactionId}/discount`,
      discount,
      {
        headers: new HttpHeaders({ 'X-API-Hash': hash }),
      },
    );
  }

  /**
   * Store extra charge in the order
   *
   * @param {ExtraCharge} extraCharge   The extra charge to apply
   * @param {string}      transactionId The transaction ID
   * @param {hash}        hash          The hash
   */
  public storeExtraCharge(extraCharge: ExtraCharge, transactionId: string, hash: string): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/api/v1/order/${transactionId}/extra-charge`, extraCharge, {
      headers: new HttpHeaders({ 'X-API-Hash': hash }),
    });
  }

  /**
   * Extracts necessary information from the given order response and stores it in local storage for later use.
   *
   * @param {OrderResponse} orderResponse the API response when placing an order
   */
  public saveOrderValuesToLocalStorage(orderResponse: OrderResponse): void {
    const patientControlValue = this.formService.checkout.get('patient').value;

    if (patientControlValue.phone) {
      this.storage.sendSMS = true;
    }

    this.storage.orderTotal = this.getTotal();
    this.storage.transactionId = orderResponse.transaction_id;
    this.storage.order = {
      has_better_lab: orderResponse.has_better_lab,
      email: orderResponse.email,
      date_of_birth: orderResponse.date_of_birth,
      hash: orderResponse.hash,
      first_name: orderResponse.first_name,
      last_name: orderResponse.last_name,
      gender: orderResponse.gender,
      transaction_id: orderResponse.transaction_id,
      lab: { zip_code: orderResponse.lab?.zip_code },
    };
    this.storage.email = orderResponse.email || patientControlValue.email;
    this.dataLayerService.pushDataLayerTracking(DataLayerCategories.Standard);
    this.storage.orderType = patientControlValue.contactVia;

    if (!this.storage.free) {
      this.storage.betterLab = !!orderResponse.has_better_lab;
    }

    this.storage.orderedTests = this.storage.tests;
    this.storage.remove(StorageKeys.Tests);

    if (this.storage.orderUpsells?.length) {
      this.dataLayerService.pushDataLayerTracking(DataLayerCategories.Upsell);
      this.storage.orderUpsells.map((upsell) => this.addUpsellTestToStorage(upsell));
    }
  }

  /**
   * Gets available upgrades for tests.
   *
   * @param {number[]} testIds The IDs of the tests to get upgrades for
   * @param {string} siteId The site ID
   * @param {number} labId The lab ID (optional)
   */
  public getAvailableUpgrades(
    testIds: number[],
    siteId: string,
    labId?: number,
  ): Observable<PaginatedResponse<Upgrade>> {
    const params = new HttpParams({
      fromObject: {
        'test_ids[]': testIds,
        site_id: siteId,
        ...(labId && { lab_id: labId }),
      },
    });

    return this.http.get<PaginatedResponse<Upgrade>>(`${this.apiUrl}/api/v2/order-upgrades`, { params });
  }

  /**
   * Gets additional items to display in the cart (order summary).
   */
  public getCartAdditionalItems(): CartAdditionalItem[] {
    return [];
  }

  /**
   * Adds the physician fee extra charge to the order.
   */
  public setPhysicianFeeExtraCharge(): void {
    this.clearPreviousPhysicianFeeExtraCharge();

    if (!environment.order.hasPhysicianFee || this.storage.baOrder?.isPhysicianFeeWaived || this.storage.free) {
      return;
    }

    const physicianExtraCharge = this.getPhysicianExtraCharge();

    if (this.storage.extraCharges.some((charge) => charge.name === physicianExtraCharge.name)) {
      return;
    }

    this.storage.extraCharges = [...this.storage.extraCharges, physicianExtraCharge];
  }

  /**
   * Gets the shipping addresses from an order.
   *
   * @param {string} transactionId the transaction ID of the order
   * @param {string} hash the hash of the order
   */
  public getShippingAddresses(transactionId: string, hash: string): Observable<{ data: ShippingAddress[] }> {
    return this.http.get<{ data: ShippingAddress[] }>(`${this.apiUrl}/api/v1/order/${transactionId}/shipping-address`, {
      headers: new HttpHeaders({ 'X-API-Hash': hash }),
    });
  }

  /**
   * Retrieves questions from the JSON file.
   */
  public getUpgradeSchema(): Promise<OrderUpgradeSchema[]> {
    return new Promise((resolve, reject) => {
      this.http.get('assets/config/order-upgrade-content.json').subscribe({
        next: (questionData: any) => resolve(questionData),
        error: () => reject('Error loading questions'),
      });
    });
  }

  /**
   * Stores a customer note for the order.
   *
   * @param {string} transactionId the transaction ID of the order
   * @param {string} hash The hash of the order
   * @param {CustomerNote} customerNote The customer note to store
   */
  public storeCustomerNote(transactionId: string, hash: string, customerNote: CustomerNote): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/api/v1/order/${transactionId}/customer-notes`, customerNote, {
      headers: new HttpHeaders({ 'X-API-Hash': hash }),
    });
  }

  /**
   * Loads the cart with the given tests, falling back to the URL or storage tests.
   *
   * @param {Test[]} tests the tests to load in the cart
   * @param {LoadCartOptions} options extra data for site-specific implementations, unused here
   *
   * @returns {Observable<AnalyteCareLoadCartResponse>} the load cart response
   */
  public loadCart(tests?: Test[], options?: LoadCartOptions): Observable<AnalyteCareLoadCartResponse> {
    const url = new URL(document.location.href);
    const request = new AnalyteCareLoadCartRequest({
      tests: this.getLoadCartTests(tests),
      coupon: this.couponCode,
      center_id:
        Number(url.searchParams.get('loc_id') || url.searchParams.get('center')) || this.storage.center?.id || null,
      state_short_name: url.searchParams.get('state') || this.storage.inHomeBookingResult?.address?.state || null,
      authToken: this.authToken,
    });

    return this.loadCartRequest(request);
  }

  /**
   * Get a Subscription to the lab selection that chooses the tests in each event.
   *
   * @returns a Subscription to resultSelected$ Observable
   */
  public listenForLabSelection(): Subscription {
    return this.results.resultSelected$.subscribe((result: Result) => {
      const request = new AnalyteCareLoadCartRequest({
        center_id: result.id,
        tests: this.storage.tests,
        coupon: this.storage.coupon?.coupon_code ?? null,
        authToken: this.authToken,
      });

      this.loadCartRequest(request).subscribe();
    });
  }

  /**
   * Parses the response by making an additional request to get the coupon details and replacing the coupon object in
   * the response.
   *
   * @param {LoadCartResponse} response the API response to parse
   *
   * @returns {Observable<AnalyteCareLoadCartResponse>} an Observable of the AnalyteCareLoadCartResponse
   */
  public parseCartResponse(response: LoadCartResponse): Observable<AnalyteCareLoadCartResponse> {
    return this.parseCoupon(response as AnalyteCareLoadCartResponse);
  }

  /**
   * Creates a LoadCartRequest from a Reorder object.
   *
   * @param {Reorder} reorder the Reorder object containing the details of the order to reorder
   */
  public getOrderRequestFromReorder(reorder: Reorder): LoadCartRequest {
    const tests = reorder.tests.map((test: OrderTest) => ({
      id: test.test_id,
      name: test.customer_tests_name,
      price: test.customer_tests_price,
      slug: null,
      type: test.customer_tests_type,
    }));

    return new AnalyteCareLoadCartRequest({
      center_id: reorder.center?.id,
      tests,
      coupon: reorder.coupon_code,
      authToken: this.authToken,
    });
  }

  /**
   * Redirects the user to the next page after placing an order.
   */
  protected redirectToNextPage(): void {
    this.router.navigateByUrl(this.orderPlacedRedirectPage);
  }

  /**
   * Map graphql response to Reorder object
   *
   * @param response The graphql response
   */
  protected mapResponseToReorder(response: any): Reorder {
    return {
      first_name: response.first_name,
      last_name: response.last_name,
      leave_msg: response.leave_message,
      send_sms: response.send_sms,
      email: response.email,
      phone: response.phone,
      sex: response.gender,
      dob: {
        date: response.date_of_birth,
      },
      tests: response.tests.filter((test: OrderTest) =>
        [CustomerTestsTypes.Individual, CustomerTestsTypes.Panel].includes(test.customer_tests_type),
      ),
      coupon_code: '10off',
      center: response.lab ? { ...response.lab, id: +response.lab.id } : null,
    };
  }

  /**
   * Adds the in-home collection booking data to the order request.
   *
   * @param {OrderRequest} orderRequest the order request to add the in-home collection booking data to
   */
  protected addInHomeCollectionBookingToOrderRequest(orderRequest: OrderRequest): void {
    const inHomeBookingResult: InHomeBookingResult = this.storage.inHomeBookingResult;

    if (!inHomeBookingResult) {
      return;
    }

    orderRequest.in_home_collection = OrderService.inHomeCollectionFromInHomeBookingResult(inHomeBookingResult);
    orderRequest.state = inHomeBookingResult.address.state;
  }

  /**
   * Creates the order request body from the form and storage variables.
   *
   * @param {string} deviceData the device data from Braintree
   * @param {NgxPaymentPayment[]} payments the payment data from the payment package
   * @param {boolean} isExternalSubscriptionOrder whether the order is billed through the external subscription
   * service, submitting all payments in a single request
   *
   * @returns {ApiOrderRequest} complete order request object with patient and address data
   */
  protected getOrderRequest(
    deviceData: string,
    payments: NgxPaymentPayment[],
    isExternalSubscriptionOrder: boolean = false,
  ): ApiOrderRequest {
    const orderRequest = this.createOrderRequestFromCheckoutForm(this.paymentService.getPrimaryPayment(payments));
    const giftCardPayment = this.paymentService.getGiftCardAsSecondaryPayment(payments);
    const chargeAmount =
      giftCardPayment && !isExternalSubscriptionOrder ? this.getTotal() - giftCardPayment.balance * 100 : null;

    if (isExternalSubscriptionOrder) {
      orderRequest.markAsExternalSubscription(giftCardPayment);
    }

    this.addOptionalOrderProperties(orderRequest, deviceData, chargeAmount);
    this.addGuardianInfoToOrderRequest(orderRequest);
    this.addInHomeCollectionBookingToOrderRequest(orderRequest);
    this.updateStorageWithOrderData(orderRequest);

    return orderRequest;
  }

  /**
   * Returns array of test IDs from stored tests.
   *
   * @returns {number[] | { test_id: string; reference_id: string }[]} test IDs for the order
   */
  protected getOrderTests(): number[] | { test_id: string; reference_id: string }[] {
    return this.storage.tests.map((test) => test.id);
  }

  /**
   * Returns coupon code from storage if available.
   *
   * @returns {string | AnalyteCareCouponDto[] | null} coupon code or null if none exists
   */
  protected getOrderCoupon(): string | AnalyteCareCouponDto[] | null {
    return this.storage.coupon?.coupon_code ?? null;
  }

  /**
   * Saves the cart response in the storage.
   *
   * @param {AnalyteCareLoadCartResponse} response the api response that contains the data to be saved
   */
  protected saveCartResponse(response: AnalyteCareLoadCartResponse): void {
    this.updateStorage(response);
  }

  /**
   * Navigates to the error page if something went wrong while reordering.
   */
  protected navigateToOrderErrorPage(): void {
    this.document.location.href = this.queryParamsService.buildRedirectWithPersistedParams(
      environment.siteUrls.tests,
      this.document.location.href,
    );
  }

  /**
   * Makes a request to the API to get a coupon by its code.
   */
  protected getCoupon(code: string): Observable<Coupon> {
    return this.http.get<Coupon>(`${this.apiUrl}/coupon/${code}`);
  }

  /**
   * Replaces the response coupon with the coupon from the API.
   *
   * @param {AnalyteCareLoadCartResponse} response the load cart response
   *
   * @returns {Observable<AnalyteCareLoadCartResponse>} an observable of the load cart response with the coupon replaced
   */
  protected parseCoupon(response: AnalyteCareLoadCartResponse): Observable<AnalyteCareLoadCartResponse> {
    if (!response.coupon) {
      return of(response);
    }

    return this.getCoupon(response.coupon?.coupon_code).pipe(
      map((couponResponse) => ({ ...response, coupon: couponResponse })),
      catchError(() => of(response)),
    );
  }

  /**
   * Gets the extra charge object for the order request.
   *
   * @returns {{ extra_charge: ExtraCharge } | {}} extra charge object or empty object if no extra charges exist
   */
  protected getExtraChargeObject(): { extra_charge: ExtraCharge } | {} {
    if (!this.extraCharges.length) {
      return {};
    }

    return {
      extra_charge: {
        name: `Extra Charge - ${this.extraCharges.map((charge) => charge.name).join(', ')}`,
        amount: this.getExtraChargesTotal(),
      },
    };
  }

  /**
   * Handles errors from the load cart request or response validation.
   *
   * If an error occurs, it will be logged to the console, and the user will be redirected to the order error page.
   *
   * @param {any} error the error to handle
   *
   * @returns {Observable<never>} an empty observable
   */
  protected handleLoadCartError(error: HttpErrorResponse): Observable<never> {
    this.errorLoggingService.logError(error);
    this.navigateToOrderErrorPage();

    return of();
  }

  /**
   * Calculates the total discount amount by adding the coupon amount applied plus the partner discount if applicable.
   *
   * @returns the amount of the discount
   */
  protected getTotalDiscount(): number {
    return this.storage.hasPartner ? this.partnerDiscount : 0;
  }

  /**
   * Adds guardian information from patient form to the order request.
   *
   * @param {OrderRequest} orderRequest the order request to modify
   */
  protected addGuardianInfoToOrderRequest(orderRequest: OrderRequest): void {
    orderRequest.parseGuardianInfo(this.checkoutForm.value.patient);
  }

  /**
   * Returns empty object since address is provided by patient later, not in order request.
   *
   * @param {Address} _addressFormValues address form values containing customer address information
   *
   * @returns {Partial<OrderRequest>} empty object since address is handled separately
   */
  protected getOrderFullAddress(_addressFormValues: Address): Partial<OrderRequest> {
    return {};
  }

  /**
   * Get the tests to be loaded in the cart, using the sorting order provided tests, URL tests, and storage tests.
   *
   * @param {Test[]} tests List of tests to be loaded in the cart.
   */
  protected getLoadCartTests(tests?: Test[]): Test[] | string[] {
    if (tests?.length) {
      return tests;
    }

    if (this.testsFromUrl.length) {
      return this.testsFromUrl.map((slug) => ({ slug: slug }) as Test);
    }

    return this.storage.tests;
  }

  /**
   * Handle the response of the load cart request.
   *
   * @param {Observable<LoadCartResponse>} observable The observable to handle the response of
   *
   * @returns {Observable<LoadCartResponse>} The observable with the response handled
   */
  private handleLoadCartResponse(observable: Observable<LoadCartResponse>): Observable<AnalyteCareLoadCartResponse> {
    return observable.pipe(
      switchMap((response) => {
        if (!response.tests?.length) {
          this.navigateToOrderErrorPage();

          return EMPTY;
        }

        return this.parseCartResponse(response).pipe(tap((response) => this.saveCartResponse(response)));
      }),
      catchError((error: HttpErrorResponse) => this.handleLoadCartError(error)),
    );
  }

  /**
   * Store the shipping address for a test.
   *
   * @param {string}   upsellName    the name of the upsell
   * @param {number[]} testIds       the test ID generated for the stored upsell
   * @param {string}   transactionId the transaction ID of the order
   * @param {string}   hash          the hash of the order
   */
  private storeShippingAddressForTests(
    upsellName: string,
    testIds: number[],
    transactionId: string,
    hash: string,
  ): Observable<ShippingAddressStoreResponse> {
    let address: any;
    switch (upsellName) {
      case UpsellSlugs.MensIntimateWash:
      case UpsellSlugs.DeliverMedication:
      case UpsellSlugs.Glp1Booster:
        address = this.consultationStorageService.getUpsellAddress(upsellName);
        break;
      default:
        return of(null);
    }

    return this.storeShippingAddress(
      new ShippingAddressStoreRequest(
        address,
        this.consultationStorageService.patient,
        testIds,
        this.treatmentTypeService.getTreatmentType(this.storage.treatmentType)?.shippingService ?? '',
      ),
      transactionId,
      hash,
    );
  }

  /**
   * Get upsell params.
   *
   * @param {number[]} testIds the ids of the tests to get upsells for
   */
  private getUpsellParams(testIds: number[]): HttpParams {
    let params = new HttpParams();
    testIds.forEach((id) => (params = params.append('tests[]', id.toString())));
    params = params.append('visibility', 'web');

    if ((environment.site as Sites) === Sites.Healthlabs) {
      params = params.append('useDefault', '1');
    }

    return params;
  }

  /**
   * Stores the data that depends on the customer tests created by the upsell batch: the shipping addresses of the
   * shippable upsells and the male BV partner treatment discount.
   *
   * Every request swallows its own failure, since forkJoin would otherwise abort the requests that are still in flight
   * as soon as one of them fails. The batch is stored and charged by the time these run, so a lost follow-up cannot be
   * recovered by storing the batch again.
   *
   * @param {OrderUpsell[]}              upsells       the upsells that were sent in the batch
   * @param {UpsellBatchStoreResponse[]} createdTests  the customer tests created for those upsells
   * @param {string}                     transactionId the transaction ID of the order
   * @param {string}                     hash          the hash of the order
   */
  private storeUpsellFollowUps(
    upsells: OrderUpsell[],
    createdTests: UpsellBatchStoreResponse[],
    transactionId: string,
    hash: string,
  ): Observable<any> {
    return forkJoin(
      [
        ...this.getShippingAddressRequests(upsells, createdTests, transactionId, hash),
        this.getMaleBVDiscountRequest(upsells, transactionId, hash),
      ].map((request) => request.pipe(catchError((error: any) => this.swallowError(error)))),
    );
  }

  /**
   * Builds one shipping address request per created customer test, since each shippable upsell has its own address.
   *
   * @param {OrderUpsell[]}              upsells       the upsells that were sent in the batch
   * @param {UpsellBatchStoreResponse[]} createdTests  the customer tests created for those upsells
   * @param {string}                     transactionId the transaction ID of the order
   * @param {string}                     hash          the hash of the order
   */
  private getShippingAddressRequests(
    upsells: OrderUpsell[],
    createdTests: UpsellBatchStoreResponse[],
    transactionId: string,
    hash: string,
  ): Observable<ShippingAddressStoreResponse>[] {
    return createdTests.map((createdTest) => {
      const upsellName = upsells.find((upsell) => upsell.id === createdTest.test_id)?.name;

      return this.storeShippingAddressForTests(upsellName, [createdTest.id], transactionId, hash);
    });
  }

  /**
   * Builds the male BV partner treatment discount request when that upsell is part of the batch.
   *
   * @param {OrderUpsell[]} upsells       the upsells that were sent in the batch
   * @param {string}        transactionId the transaction ID of the order
   * @param {string}        hash          the hash of the order
   */
  private getMaleBVDiscountRequest(
    upsells: OrderUpsell[],
    transactionId: string,
    hash: string,
  ): Observable<ShippingAddressStoreResponse> {
    const maleBVUpsellId = environment.consultationRequestUpsells[UpsellSlugs.MaleBVPartnerTreatment]?.id;
    const maleBVUpsell = upsells.find(
      (upsell) => !!maleBVUpsellId && upsell.id === maleBVUpsellId && !!upsell.discount,
    );

    return maleBVUpsell ? this.storeMaleBVDiscount(maleBVUpsell.discount, transactionId, hash) : of(null);
  }

  /**
   * Updates the address information for a free order.
   */
  private updateAddressForFreeOrder(): void {
    const addressContactInformation = this.formService.addressContactInformation;
    const addressUpdateRequest = new AddressUpdateRequest(addressContactInformation);
    addressUpdateRequest.agreeTerms = true;

    if (addressContactInformation.get('phone.sms_allowed').value) {
      this.storage.sendSMS = true;
    }

    this.storeAddress(addressUpdateRequest)
      .pipe(
        catchError((error) => {
          this.errorLoggingService.logError(error);

          return of(null);
        }),
      )
      .subscribe(() => {
        this.storage.addressComplete = true;
        this.router.navigateByUrl('/order-complete.php');
      });
  }

  /**
   * Creates an OrderRequest object from the current checkout form data.
   *
   * @param {NgxPaymentPayment} payment the payment information to include in the order request
   *
   * @returns {OrderRequest} a new OrderRequest instance
   */
  private createOrderRequestFromCheckoutForm(payment: NgxPaymentPayment): OrderRequest {
    const checkout = this.checkoutForm.value;
    const { patient } = checkout;

    return new OrderRequest(
      !environment.order.forceEmailResults ? patient.contactVia : 'email',
      this.getOrderCoupon(),
      this.storage.getCookie('cje') || this.storage.getCookie('cjevent_dc'),
      new Date(patient.birthday.year, patient.birthday.month - 1, patient.birthday.day)
        .toISOString()
        .slice(0, 10)
        .replace('T', ' '),
      patient.firstName,
      patient.sex,
      this.getOrderLabId(),
      patient.lastName,
      environment.site,
      this.getOrderTests(),
      this.storage.userId,
      payment,
    );
  }

  /**
   * Adds optional properties to the order request.
   *
   * @param {OrderRequest} orderRequest the order request to modify
   * @param {string} deviceData the device data from Braintree
   * @param {number | null} chargeAmount the amount to charge for the order, if different from the invoice amount
   */
  private addOptionalOrderProperties(
    orderRequest: OrderRequest,
    deviceData: string,
    chargeAmount: number | null = null,
  ): void {
    const { patient, address } = this.checkoutForm.value;

    const optionalProps = {
      deviceData: deviceData || undefined,
      leave_message: !environment.order.forceEmailResults ? (patient.voicemail_allowed ? 'YES' : 'NO') : undefined,
      phone: patient.phone || undefined,
      email: patient.email || undefined,
      partner_email: this.hasPartner() && patient.partnerEmail && !this.storage.free ? patient.partnerEmail : undefined,
      upsells: this.storage.orderUpsells?.length ? this.storage.orderUpsells.map((upsell) => upsell.id) : undefined,
      charge_amount: chargeAmount ?? undefined,
      ...this.getExtraChargeObject(),
      ...this.getOrderFullAddress(address),
    };

    Object.entries(optionalProps).forEach(([key, value]) => {
      if (value !== undefined) {
        orderRequest[key] = value;
      }
    });
  }

  /**
   * Updates storage with patient and order information.
   *
   * @param {OrderRequest} orderRequest the complete order request
   */
  private updateStorageWithOrderData(orderRequest: OrderRequest): void {
    const { patient } = this.checkoutForm.value;

    this.storage.patient = {
      name: `${patient.firstName} ${patient.lastName}`,
      email: patient.email || '',
      phone: patient.phone || '',
    };
    this.storage.hasEmail = !!patient.email;
    this.storage.paymentType = orderRequest.payment_type;
  }

  /**
   * Gets the physician fee item.
   */
  private getPhysicianExtraCharge(): ExtraCharge {
    return {
      name: 'Physician Fee',
      tooltip:
        'The WMG Service Fee is collected on behalf of Wilcrest Medical Group (WMG) and its affiliated professional' +
        ' entities for independent physician oversight of lab testing including ordering testing if appropriate, as ' +
        'well as providing physician follow-up services. Terms of Service are non-refundable. To learn more about WMG' +
        ' services, view their <a href="https://www.wilcrestmedicalgroup.com/terms-conditions/" class="has-text-weight-bold" target="_blank">Terms of Service and Privacy Policy.</a>',
      amount: environment.order.physicianFeeInCents,
    };
  }

  /**
   * Removes the physician fee extra charge from the storage if it exists.
   */
  private clearPreviousPhysicianFeeExtraCharge(): void {
    this.storage.extraCharges = this.storage.extraCharges.filter(
      (charge) => charge.name !== this.getPhysicianExtraCharge().name,
    );
  }

  /**
   * Checks whether the given value is null, undefined, or NaN.
   *
   * @param {any} value the number to validate
   *
   * @returns {boolean} whether the value is invalid
   */
  private isInvalidNumber(value: any): boolean {
    return value === null || value === undefined || isNaN(value);
  }

  /**
   * Logs an error to the error logging service with item and optional collection context.
   *
   * @param {string} message description of the error condition
   * @param {object} item the specific item that caused the error
   * @param {object[]} collection the full collection the item belongs to, for additional context
   */
  private logItemError(message: string, item: object, collection?: object[]): void {
    this.errorLoggingService.logError({
      message,
      item: JSON.stringify(item),
      ...(collection !== undefined && { collection: JSON.stringify(collection) }),
    });
  }
}
